/**
 * useMonthlyPerformance - Hook for fetching monthly performance data
 *
 * Lazy-loads monthly performance data from the backend when needed.
 * Data is only fetched when:
 * 1. Optimization results are available
 * 2. User is on a tab that needs the data (Financial, Analysis, or Results)
 *
 * @module hooks/useMonthlyPerformance
 */

import { useState, useEffect } from 'react';
import { debug } from '../utils/debug';
import monthlyPerformanceService from '../services/monthlyPerformanceService';

/**
 * @typedef {Object} MonthlyPerformanceOptions
 * @property {Object} optimization - Optimization state with results
 * @property {Object} scenarioForm - Scenario form state
 * @property {number} currentTab - Current active tab index
 */

/**
 * @typedef {Object} MonthlyPerformanceReturn
 * @property {Object|null} monthlyPerformanceData - Monthly performance data or null
 * @property {boolean} isLoading - Whether data is currently being fetched
 */

/**
 * Hook for managing monthly performance data fetching
 *
 * @param {MonthlyPerformanceOptions} options
 * @returns {MonthlyPerformanceReturn}
 */
export function useMonthlyPerformance({ optimization, scenarioForm, currentTab }) {
    const [monthlyPerformanceData, setMonthlyPerformanceData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch monthly performance data when results are available AND user is on a tab that needs it
    // This lazy-loads the data to avoid slow initial scenario load
    useEffect(() => {
        const fetchData = async () => {
            if (!optimization?.results?.outputs) {
                debug.log('📊 [useMonthlyPerformance] No optimization outputs - clearing monthly data');
                setMonthlyPerformanceData(null);
                return;
            }

            // Only fetch if user is on Financial (4), Analysis (5), or Results (6) tab
            // This speeds up initial load significantly
            if (currentTab < 4) {
                debug.log('📊 [useMonthlyPerformance] Deferring monthly data fetch (not on financial/results tab)');
                return;
            }

            // Skip if already have data for this optimization run
            if (monthlyPerformanceData && monthlyPerformanceData._runUuid === optimization.results?.run_uuid) {
                debug.log('📊 [useMonthlyPerformance] Monthly data already loaded for this run');
                return;
            }

            setIsLoading(true);
            try {
                const effectiveTariff = scenarioForm.customTariffData || scenarioForm.selectedTariffData;
                debug.log('📊 [useMonthlyPerformance] Fetching monthly performance data...', {
                    hasTariff: !!effectiveTariff,
                    hasCaDerConfig: !!scenarioForm.caDerConfig
                });

                const data = await monthlyPerformanceService.fetchMonthlyPerformance(
                    optimization.results,
                    effectiveTariff,
                    { caDerConfig: scenarioForm.caDerConfig }
                );

                debug.log('📊 [useMonthlyPerformance] Monthly performance data received:', {
                    hasBauCosts: !!data?.bau_monthly_costs,
                    bauMonthlyCount: data?.bau_monthly_costs?.length,
                    projectMonthlyCount: data?.project_monthly_costs?.length,
                    hasDerCharges: !!data?.monthly_der_charges?.length
                });

                // Tag the data with the run UUID so we know which run it belongs to
                setMonthlyPerformanceData({ ...data, _runUuid: optimization.results?.run_uuid });
            } catch (error) {
                debug.error('[useMonthlyPerformance] Monthly performance fetch failed:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [
        optimization?.results,
        scenarioForm.customTariffData,
        scenarioForm.selectedTariffData,
        scenarioForm.caDerConfig,
        currentTab,
        monthlyPerformanceData
    ]);

    return {
        monthlyPerformanceData,
        isLoading,
    };
}

export default useMonthlyPerformance;
